import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  AlertCircle, 
  CheckCircle,
  ChevronDown,
  Utensils,
  X,
  Check,
  ArrowUpDown,
  MoreVertical,
  Copy
} from 'lucide-react';
import RecipeForm from './RecipeForm.jsx';
import RecipeDetails from './RecipeDetails.jsx';
import Modal from '../Layout/Modal.jsx';
import AllergenBadge from '../Allergens/AllergenBadge.jsx';
import { recipesAPI, uploadAPI } from '../../utils/api';
import { formatRecipeSaveError } from '../../utils/recipeSaveErrors.js';
import { useCurrency } from '../../contexts/CurrencyContext';

const CategoryRecipes = () => {
  const { t, i18n } = useTranslation();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [showRecipeForm, setShowRecipeForm] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showRecipeDetails, setShowRecipeDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [recipeToDelete, setRecipeToDelete] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [isBulkDeleteMode, setIsBulkDeleteMode] = useState(false);
  const [selectedRecipes, setSelectedRecipes] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [duplicatingRecipeId, setDuplicatingRecipeId] = useState(null);
  const [sortOrder, setSortOrder] = useState('id');

  useEffect(() => {
    loadData();
  }, [categoryId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdownId(null);
    };

    if (openDropdownId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdownId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [recipesData, categoriesData] = await Promise.all([
        recipesAPI.getByCategory(categoryId),
        recipesAPI.getCategories()
      ]);
      
      setRecipes(recipesData);
      setCategories(categoriesData);
      
      // Find current category
      const category = categoriesData.find(cat => cat.id === parseInt(categoryId));
      console.log('CategoryRecipes - categoryId:', categoryId, 'parsed:', parseInt(categoryId), 'found category:', category);
      setCurrentCategory(category);
    } catch (error) {
      console.error('Error loading data:', error);
      setErrorMessage(t('recipes.failedToLoadRecipesData'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRecipe = async (recipeData) => {
    try {
      console.log('Recipe data being saved:', recipeData);
      console.log('Image URL in recipe data:', recipeData.image_url);
      
      if (editingRecipe) {
        // Update existing recipe
        console.log('Updating existing recipe:', editingRecipe.id);
        const updatedRecipe = await recipesAPI.update(editingRecipe.id, recipeData);
        console.log('Updated recipe response:', updatedRecipe);
        
        // Reload all recipes to ensure dependent recipes are updated
        // This is necessary because changing servings/ingredients can affect other recipes
        await loadData();
        
        setSuccessMessage(t('recipes.recipeUpdatedSuccessfully'));
      } else {
        // Add new recipe
        console.log('Creating new recipe with category:', categoryId);
        const newRecipe = await recipesAPI.create({
          ...recipeData,
          category_id: categoryId
        });
        console.log('New recipe response:', newRecipe);
        
        // Reload all recipes to ensure the new recipe is properly calculated
        await loadData();
        
        setSuccessMessage(t('recipes.recipeCreatedSuccessfully'));
      }
      
      setShowRecipeForm(false);
      setEditingRecipe(null);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving recipe:', error);
      setErrorMessage(formatRecipeSaveError(error, t));
      setShowErrorModal(true);
    }
  };

  // Function to extract public ID from Cloudinary URL
  const extractPublicIdFromUrl = (url) => {
    if (!url) return null;
    try {
      // Extract public ID from Cloudinary URL
      // Format: https://res.cloudinary.com/cloud_name/image/upload/v123456789/folder/image.jpg
      const match = url.match(/\/upload\/[^\/]+\/(.+)$/);
      if (match) {
        let publicId = match[1];
        // Remove file extension from public ID (Cloudinary public IDs don't include extensions)
        publicId = publicId.replace(/\.[^/.]+$/, '');
        return publicId;
      }
      return null;
    } catch (error) {
      console.error('Error extracting public ID from URL:', error);
      return null;
    }
  };

  // Function to clean up recipe image from Cloudinary
  const cleanupRecipeImage = async (imageUrl) => {
    if (!imageUrl) return;
    try {
      const imagePublicId = extractPublicIdFromUrl(imageUrl);
      if (imagePublicId) {
        console.log('🧹 Cleanup: Deleting recipe image:', imagePublicId);
        await uploadAPI.deleteImage(imagePublicId);
        console.log('✅ Cleanup: Recipe image cleaned up successfully');
      }
    } catch (error) {
      console.error('❌ Cleanup: Failed to cleanup recipe image:', error);
    }
  };

  // Function to clean up procedure step images from Cloudinary
  const cleanupProcedureImages = async (procedures) => {
    if (!procedures || !Array.isArray(procedures)) return;
    
    for (const step of procedures) {
      if (step.image_url) {
        try {
          const imagePublicId = extractPublicIdFromUrl(step.image_url);
          if (imagePublicId) {
            console.log('🧹 Cleanup: Deleting procedure image:', imagePublicId);
            await uploadAPI.deleteImage(imagePublicId);
            console.log('✅ Cleanup: Procedure image cleaned up successfully');
          }
        } catch (error) {
          console.error('❌ Cleanup: Failed to cleanup procedure image:', error);
        }
      }
    }
  };

  const handleDeleteRecipe = async (recipeId) => {
    const recipeRecord = recipes.find(r => r.id === recipeId);
    setRecipeToDelete(recipeRecord || null);

    setConfirmAction(() => async () => {
      try {
        // Fetch full recipe data (including procedures) before deletion
        console.log('🔍 Fetching full recipe data for cleanup before deletion...');
        const fullRecipeData = await recipesAPI.getById(recipeId);
        console.log('📋 Full recipe data fetched:', fullRecipeData);
        
        // Delete the recipe from database
        await recipesAPI.delete(recipeId);
        
        // Reload all recipes to ensure dependent recipes are updated
        // This is necessary because deleting a recipe can affect other recipes that use it
        await loadData();
        
        setSuccessMessage(`${t('recipes.recipeDeletedSuccessfully')}\n\n${t('recipes.allDependentRecipesRecalculated')}`);
        setShowSuccessModal(true);
        
        // Clean up images in the background
        if (fullRecipeData) {
          // Clean up main recipe image
          if (fullRecipeData.image_url) {
            cleanupRecipeImage(fullRecipeData.image_url).catch(error => {
              console.error('Background cleanup failed for recipe image:', error);
            });
          }
          
          // Clean up procedure step images
          if (fullRecipeData.procedures && Array.isArray(fullRecipeData.procedures)) {
            cleanupProcedureImages(fullRecipeData.procedures).catch(error => {
              console.error('Background cleanup failed for procedure images:', error);
            });
          }
        }
      } catch (error) {
        if (error.message && error.message.includes('Cannot delete recipe that is being used as ingredient in other recipes')) {
          // Don't log expected constraint violations - they're handled gracefully
          setErrorMessage(error.message);
        } else {
          // Log unexpected errors
          console.error('Error deleting recipe:', error);
          setErrorMessage(t('recipes.failedToDeleteRecipe'));
        }
        setShowErrorModal(true);
      }
    });
    setShowConfirmModal(true);
  };

  const enterBulkDeleteMode = () => {
    setIsBulkDeleteMode(true);
    setSelectedRecipes(new Set());
  };

  const cancelBulkDeleteMode = () => {
    setIsBulkDeleteMode(false);
    setSelectedRecipes(new Set());
  };

  const handleSelectRecipe = (recipeId) => {
    const next = new Set(selectedRecipes);
    if (next.has(recipeId)) next.delete(recipeId);
    else next.add(recipeId);
    setSelectedRecipes(next);
  };

  const handleSelectAll = () => {
    const current = filteredRecipes;
    if (selectedRecipes.size === current.length) setSelectedRecipes(new Set());
    else setSelectedRecipes(new Set(current.map(r => r.id)));
  };

  const handleEditRecipe = (recipe) => {
    setEditingRecipe(recipe);
    setShowRecipeForm(true);
  };

  const handleDuplicateRecipe = async (recipe) => {
    if (duplicatingRecipeId) return;
    try {
      setDuplicatingRecipeId(recipe.id);
      setOpenDropdownId(null);
      await recipesAPI.duplicate(recipe.id);
      await loadData();
      setSuccessMessage(t('recipes.recipeDuplicatedSuccessfully'));
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error duplicating recipe:', error);
      setErrorMessage(t('recipes.failedToDuplicateRecipe'));
      setShowErrorModal(true);
    } finally {
      setDuplicatingRecipeId(null);
    }
  };

  const handleViewRecipe = (recipe) => {
    setSelectedRecipe(recipe);
    setShowRecipeDetails(true);
  };

  const handleCategoryChange = (newCategoryId) => {
            navigate(`/dish-costing/recipes/category/${newCategoryId}`);
  };

  const filteredRecipes = recipes
    .filter(recipe => 
      recipe.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recipe.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recipe.id?.toString().includes(searchTerm)
    )
    .sort((a, b) => {
      if (sortOrder === 'id') return (a.id || 0) - (b.id || 0);
      return (a.name_en || '').localeCompare(b.name_en || '');
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background text-foreground min-h-screen p-6">
      <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('recipes.directMaterialCosts')}</h1>
          <p className="mt-2 text-muted-foreground">
            {t('recipes.recipesInCategory', { category: i18n.language === 'ar' 
              ? (currentCategory?.name_ar || currentCategory?.name_en || t('recipes.recipes'))
              : (currentCategory?.name_en || t('recipes.recipes'))
            })}
          </p>
        </div>
        
        {/* Action Buttons */}
        <div className={`flex justify-end mt-4 sm:mt-0 ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
          {isBulkDeleteMode ? (
            <>
              <button onClick={cancelBulkDeleteMode} className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-secondary hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors">
                <X className="h-4 w-4 mr-2" />
                {t('common.cancel')}
              </button>
              <button onClick={handleSelectAll} className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-secondary hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors">
                <Check className="h-4 w-4 mr-2" />
                {selectedRecipes.size === filteredRecipes.length ? t('common.deselectAll') : t('common.selectAll')}
              </button>
              <button onClick={() => setShowBulkDeleteConfirm(true)} disabled={selectedRecipes.size === 0} className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <Trash2 className="h-4 w-4 mr-2" />
                {t('common.deleteSelected')} ({selectedRecipes.size})
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setShowRecipeForm(true)} className="inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring" title="Add New Recipe">
                <Plus className="h-4 w-4 mr-2" />
                {t('recipes.addNewRecipe')}
              </button>
              <button onClick={enterBulkDeleteMode} className="inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring" title={t('common.bulkDelete')}>
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dish-costing/recipes')}
            className="inline-flex items-center justify-center px-3 py-2 border border-muted rounded-lg text-sm font-medium text-muted-foreground bg-muted hover:bg-muted/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-muted transition-colors"
          title={t('recipes.backToCategories')}
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        
        {/* Category Dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
              className="inline-flex items-center px-4 py-2 border border-muted rounded-lg text-sm font-medium text-muted-foreground bg-muted hover:bg-muted/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-muted transition-colors min-w-[200px] justify-between"
          >
            <span className={i18n.language === 'ar' ? 'font-arabic' : ''}>
              {i18n.language === 'ar' 
                ? (currentCategory?.name_ar || currentCategory?.name_en || (loading ? t('recipes.loading') : t('recipes.selectCategory')))
                : (currentCategory?.name_en || (loading ? t('recipes.loading') : t('recipes.selectCategory')))
              }
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
          </button>
          
          {showCategoryDropdown && (
            <div className="absolute top-full left-0 mt-1 w-full bg-card border border-border rounded-lg shadow-lg z-10">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    handleCategoryChange(category.id);
                    setShowCategoryDropdown(false);
                  }}
                  className={`w-full text-left px-4 py-2 hover:bg-muted transition-colors ${
                    category.id === parseInt(categoryId) ? 'bg-primary/10 text-primary' : 'text-card-foreground'
                  }`}
                  title={i18n.language === 'ar' ? (category.name_ar || category.name_en) : category.name_en}
                >
                  <span className={`truncate block ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
                    {i18n.language === 'ar' ? (category.name_ar || category.name_en) : category.name_en}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Search and Sort */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Search Input */}
        <div className="flex-1 max-w-lg">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder={t('recipes.searchByRecipeNameOrId')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 pl-10 border border-border rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors bg-background text-foreground"
              style={{ paddingLeft: '2.75rem' }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Sort Button */}
        <button
          onClick={() => setSortOrder(sortOrder === 'id' ? 'alphabetical' : 'id')}
          className="inline-flex items-center justify-center h-10 px-3 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
          title={sortOrder === 'id' ? t('recipes.sortById') : t('recipes.sortByName')}
        >
          <ArrowUpDown className="h-4 w-4" />
        </button>
      </div>

      {/* Recipes Grid */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-card-foreground">
            {i18n.language === 'ar' 
              ? `أطباق ${currentCategory?.name_ar || currentCategory?.name_en || t('recipes.recipes')}`
              : `${currentCategory?.name_en || t('recipes.recipes')} ${t('recipes.recipes')}`
            }
          </h3>
          <div className="text-sm text-muted-foreground">
            {t('recipes.recipesTotal', { count: filteredRecipes.length })}
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6 gap-6">
         {filteredRecipes.map((recipe) => (
          <div
            key={recipe.id}
            onClick={() => handleViewRecipe(recipe)}
            className="group cursor-pointer relative"
          >
            <div className={`bg-card rounded-2xl border border-border transition-all duration-300 transform overflow-hidden flex flex-col h-full ${
              isBulkDeleteMode && selectedRecipes.has(recipe.id)
                ? 'border-primary ring-2 ring-primary/20 bg-primary/5'
                : 'hover:-translate-y-1'
            }`}>
              {/* Recipe Image Header */}
              <div className="relative h-28 bg-input flex items-center justify-center overflow-hidden border-b border-border">
                  {isBulkDeleteMode && (
                    <div className="absolute top-3 right-3 z-10">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSelectRecipe(recipe.id); }}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          selectedRecipes.has(recipe.id) ? 'bg-primary border-primary text-primary-foreground' : 'bg-card border-border hover:border-primary'
                        }`}
                      >
                        {selectedRecipes.has(recipe.id) && (
                          <Check className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  )}

                  {/* Actions Dropdown Button */}
                  {!isBulkDeleteMode && (
                    <div className="absolute top-3 right-3 z-10">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdownId(openDropdownId === recipe.id ? null : recipe.id);
                        }}
                        className="w-8 h-8 rounded-full border border-border hover:border-border flex items-center justify-center transition-all hover:bg-muted dark:hover:bg-muted bg-muted/50 dark:bg-[rgba(10,18,25,0.5)]"
                      >
                        <MoreVertical className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </div>
                  )}
                {recipe.image_url ? (
                  <img
                    src={recipe.image_url}
                    alt={recipe.name_en}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-card flex items-center justify-center">
                    <Utensils className="h-16 w-16 text-border" />
                  </div>
                )}
                
                {/* Recipe ID Badge */}
                <div className="absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium text-muted-foreground bg-muted/50 dark:bg-[rgba(10,18,25,0.5)]">
                  {recipe.id}
                </div>
                
                {/* Pricing Indicator */}
                {recipe.has_pricing !== false && (
                  <div className="absolute bottom-3 right-3 px-2 py-1 rounded-full text-xs font-medium text-orange-600 flex items-center gap-1">
                    🅿️
                  </div>
                )}
                
                {/* View Details Overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="bg-card px-4 py-2 rounded-lg text-sm font-medium text-card-foreground">
                    {t('common.clickToViewDetails')}
                  </div>
                </div>
              </div>
              
              {/* Content Section */}
              <div className="p-3 space-y-2 flex-1">
                {/* Recipe Names */}
                <div className="text-center space-y-1 -mt-2 border-b border-border pb-2 -mx-3 px-3">
                  <h3 className="font-bold text-card-foreground text-lg group-hover:text-primary transition-colors truncate" title={recipe.name_en}>
                    {recipe.name_en}
                  </h3>
                  {recipe.name_ar && (
                    <p className="text-sm text-muted-foreground font-arabic truncate" title={recipe.name_ar}>
                      {recipe.name_ar}
                    </p>
                  )}
                </div>

                {/* Allergen Section */}
                <div className="h-[48px] flex justify-center items-center">
                  {recipe.computed_allergens && recipe.computed_allergens.length > 0 ? (
                    <AllergenBadge 
                      allergens={recipe.computed_allergens}
                      variant="warning"
                      size="xs"
                      showCount={true}
                      maxDisplay={2}
                    />
                  ) : (
                    <div className="text-xs text-muted-foreground">{t('common.noAllergens')}</div>
                  )}
                </div>
                
                {/* Servings Info */}
                <div className="space-y-1">
                                     <div className="flex items-center justify-center">
                     <span className="text-sm text-card-foreground">
                       {recipe.servings || 0} {t('recipes.servings')}
                     </span>
                   </div>
                  
                </div>
                
                {/* Cost Information */}
                <div className="space-y-1">
                  {/* Total Cost */}
                  <div className="p-2 text-center">
                    <div className="mb-1">
                      <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{t('recipes.totalCost')}</span>
                    </div>
                    <div className="text-sm font-bold text-card-foreground">
                      {formatCurrency(recipe.total_cost || 0)}
                    </div>
                  </div>
                  
                </div>
              </div>
              
              {/* Cost Per Serving */}
              <div className="p-2 text-center -mx-3 bg-[#B3E8F2] dark:bg-[#2A4A5A]">
                <div className="mb-1">
                  <span className="text-xs text-card-foreground font-medium uppercase tracking-wide">{t('recipes.totalCostPerServing')}</span>
                </div>
                <div className="text-sm font-bold text-card-foreground">
                  {formatCurrency(recipe.cost_per_serving || 0)}
                </div>
              </div>
              
            </div>
            
            {/* Dropdown Menu - Outside card container */}
            {!isBulkDeleteMode && openDropdownId === recipe.id && (
              <div className="absolute top-12 right-3 bg-card rounded-lg shadow-lg border border-border py-1 min-w-[120px] z-50">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditRecipe(recipe);
                    setOpenDropdownId(null);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-card-foreground hover:bg-muted flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  {t('recipes.edit')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDuplicateRecipe(recipe);
                  }}
                  disabled={duplicatingRecipeId === recipe.id}
                  className="w-full px-3 py-2 text-left text-sm text-card-foreground hover:bg-muted flex items-center gap-2 disabled:opacity-50"
                >
                  <Copy className="h-4 w-4" />
                  {t('recipes.duplicate')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteRecipe(recipe.id);
                    setOpenDropdownId(null);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-destructive hover:bg-muted flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  {t('recipes.delete')}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      </div>

      {/* Empty State */}
      {filteredRecipes.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">
            {searchTerm ? t('recipes.noRecipesFound') : t('recipes.noRecipesInCategory')}
          </h3>
          <p className="text-muted-foreground mb-6">
            {searchTerm 
              ? t('recipes.tryAdjustingSearchTerms') 
              : t('recipes.noDishesAddedYet')
            }
          </p>
        </div>
      )}

      {/* Recipe Form Modal */}
      {showRecipeForm && (
        <RecipeForm
          recipe={editingRecipe}
          categories={categories}
          onSave={handleSaveRecipe}
          onCancel={() => {
            setShowRecipeForm(false);
            setEditingRecipe(null);
          }}
        />
      )}

      {/* Recipe Details Modal */}
      {showRecipeDetails && selectedRecipe && (
        <RecipeDetails
          recipe={selectedRecipe}
          onClose={() => {
            setShowRecipeDetails(false);
            setSelectedRecipe(null);
          }}
        />
      )}

      {/* Error Modal — elevated so it appears above the recipe form */}
      <Modal isOpen={showErrorModal} onClose={() => setShowErrorModal(false)} overlayClassName="modal-overlay-front">
        <div className="flex items-center mb-6">
          <AlertCircle className="h-6 w-6 text-destructive mr-3" />
          <h2 className="text-lg font-semibold text-card-foreground">{t('common.error')}</h2>
        </div>
        <div className="mb-8">
          <div className="text-card-foreground whitespace-pre-line">{errorMessage}</div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={() => setShowErrorModal(false)}
            className="inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
            title={t('common.ok')}
          >
            <Check className="h-4 w-4" />
          </button>
        </div>
      </Modal>

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmModal} onClose={() => {
        setShowConfirmModal(false);
        setRecipeToDelete(null);
      }}>
        <div className="flex items-center mb-6">
          <AlertCircle className="h-6 w-6 text-destructive mr-3" />
          <h2 className="text-lg font-semibold text-card-foreground">{t('common.deleteRecipe')}</h2>
        </div>
        <div className="mb-8">
          <p className="text-card-foreground">
            {t('common.confirmDeleteRecipe', { name: recipeToDelete?.name_en || '' })}
          </p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => {
              setShowConfirmModal(false);
              setRecipeToDelete(null);
            }}
            className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={async () => {
              setShowConfirmModal(false);
              setRecipeToDelete(null);
              if (confirmAction) {
                await confirmAction();
              }
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
          >
            {t('common.deleteRecipe')}
          </button>
        </div>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal isOpen={showBulkDeleteConfirm} onClose={() => setShowBulkDeleteConfirm(false)}>
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-card-foreground mb-3">{t('common.confirmBulkDelete')}</h2>
          <p className="text-card-foreground">
            {t('common.confirmDeleteSelectedRecipes', { count: selectedRecipes.size })}
          </p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setShowBulkDeleteConfirm(false)}
            className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={async () => {
              setShowBulkDeleteConfirm(false);
              const ids = Array.from(selectedRecipes);
              
              // Fetch full recipe data (including procedures) for all recipes to be deleted
              console.log('🔍 Fetching full recipe data for bulk cleanup before deletion...');
              const fullRecipesData = await Promise.allSettled(
                ids.map(id => recipesAPI.getById(id))
              );
              
              const results = await Promise.allSettled(ids.map(id => recipesAPI.delete(id)));
              const successful = results.filter(r => r.status === 'fulfilled').length;
              const failed = results.filter(r => r.status === 'rejected');
              
              // Get recipe names for failed deletions
              const failedRecipeNames = [];
              const failedRecipeIds = [];
              failed.forEach((result, index) => {
                const recipeId = ids[index];
                const recipe = recipes.find(r => r.id === recipeId);
                if (recipe) {
                  failedRecipeNames.push(recipe.name_en);
                  failedRecipeIds.push(recipeId);
                }
              });
              
              if (successful > 0) {
                // Reload all recipes to ensure dependent recipes are updated
                // This is necessary because deleting recipes can affect other recipes that use them
                await loadData();
                
                let successMessage = t('messages.bulkRecipesDeleted', { count: successful });
                
                if (failed.length > 0) {
                  successMessage += `\n\n${failed.length} recipe${failed.length !== 1 ? 's' : ''} could not be deleted:`;
                  failedRecipeNames.forEach((name, index) => {
                    const reason = failed[index].reason?.message?.includes('Cannot delete recipe that is being used as ingredient') 
                      ? ' (used as ingredient in other recipes)' 
                      : ' (unknown error)';
                    successMessage += `\n• ${name}${reason}`;
                  });
                }
                
                successMessage += `\n\n${t('recipes.allDependentRecipesRecalculated')}`;
                setSuccessMessage(successMessage);
                setShowSuccessModal(true);
                
                // Clean up images for successfully deleted recipes in the background
                fullRecipesData.forEach((fullRecipeResult, index) => {
                  if (fullRecipeResult.status === 'fulfilled' && results[index].status === 'fulfilled') {
                    const fullRecipe = fullRecipeResult.value;
                    console.log('🧹 Cleaning up images for recipe:', fullRecipe.id);
                    
                    // Clean up main recipe image
                    if (fullRecipe.image_url) {
                      cleanupRecipeImage(fullRecipe.image_url).catch(error => {
                        console.error('Background cleanup failed for recipe image:', error);
                      });
                    }
                    
                    // Clean up procedure step images
                    if (fullRecipe.procedures && Array.isArray(fullRecipe.procedures)) {
                      cleanupProcedureImages(fullRecipe.procedures).catch(error => {
                        console.error('Background cleanup failed for procedure images:', error);
                      });
                    }
                  }
                });
              } else if (failed.length > 0) {
                // No recipes were deleted, but some failed
                let errorMessage = `No recipes could be deleted. ${failed.length} recipe${failed.length !== 1 ? 's' : ''} failed:`;
                failedRecipeNames.forEach((name, index) => {
                  const reason = failed[index].reason?.message?.includes('Cannot delete recipe that is being used as ingredient') 
                    ? ' (used as ingredient in other recipes)' 
                    : ' (unknown error)';
                  errorMessage += `\n• ${name}${reason}`;
                });
                setErrorMessage(errorMessage);
                setShowErrorModal(true);
              }
              setSelectedRecipes(new Set());
              setIsBulkDeleteMode(false);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
          >
            {t('common.delete')}
          </button>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)}>
        <div className="flex items-center mb-6">
          <CheckCircle className="h-6 w-6 text-chart-3 mr-3" />
          <h2 className="text-lg font-semibold text-card-foreground">{t('common.success')}</h2>
        </div>
        <div className="mb-8">
          <div className="text-card-foreground whitespace-pre-line">{successMessage}</div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={() => setShowSuccessModal(false)}
            className="inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
            title={t('common.ok')}
          >
            <Check className="h-4 w-4" />
          </button>
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default CategoryRecipes; 